import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { Filter, MousePointerClick, X } from "lucide-react";
import { subscribe, getActive, removeFilter, clearAll, clearPicks } from "../../lib/quickFilter";

// Floating chip bar for the Quick Filter (see lib/quickFilter.js) — both the
// right-click one and the column-header panel (ColumnFilterMenu.jsx). Shows
// the active column filters and lets you drop them one-by-one or all at once.
// Mounted once in the ERP shell.
const QuickFilterBar = () => {
  const [active, setActive] = useState(getActive);
  const location = useLocation();

  useEffect(() => subscribe(setActive), []);

  // The filter is DOM-scoped to the table you applied it on. Leaving the route
  // — which includes switching ERP tabs, since that changes the URL — drops it
  // so a stale filter never lingers on a page you're no longer looking at.
  useEffect(() => { clearAll(); clearPicks(); }, [location.pathname, location.search]);

  // Esc clears all active filters and the cell selection (page back to normal).
  // Bubble phase, so an open context menu — whose Esc handler runs in capture
  // and stops the event — takes priority and just closes itself.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      const snap = getActive();
      if (snap.active) clearAll();
      if (snap.pickCount) clearPicks();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!active.active && !active.pickCount) return null;

  return createPortal(
    <div className="fixed z-[180] bottom-4 left-1/2 -translate-x-1/2 max-w-[92vw] flex items-center gap-2 rounded-full border border-border bg-popover/95 backdrop-blur px-2.5 py-1.5 shadow-xl animate-in fade-in slide-in-from-bottom-2 duration-150">
      <Filter className="w-3.5 h-3.5 text-primary flex-shrink-0" />
      {active.active && (
        <span className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide flex-shrink-0">
          Filtered
        </span>
      )}
      {/* Alt + right-click selection lives alongside the filters — you build it
          first, then act on it from the Filter flyout's Math / Compare tabs. */}
      {active.pickCount > 0 && (
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-xs whitespace-nowrap flex-shrink-0">
          <MousePointerClick className="w-3 h-3" />
          <span className="font-medium">{active.pickCount} selected</span>
          <button
            type="button"
            onClick={() => clearPicks()}
            title="Clear selection"
            className="ml-0.5 rounded-full p-0.5 hover:bg-primary/20 transition-colors"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      )}
      <div className="flex items-center gap-1.5 overflow-x-auto scrollbar-hide">
        {active.filters.map((f) => (
          <span
            key={f.key}
            className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs whitespace-nowrap"
          >
            <span className="text-muted-foreground max-w-[140px] truncate">{f.label}:</span>
            <span className="font-medium text-foreground max-w-[180px] truncate">{f.summary}</span>
            <button
              type="button"
              onClick={() => removeFilter(f.key)}
              title={`Remove ${f.label} filter`}
              className="ml-0.5 rounded-full p-0.5 text-muted-foreground hover:bg-border hover:text-foreground transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      {active.filters.length > 1 && (
        <button
          type="button"
          onClick={() => clearAll()}
          className="text-xs font-medium text-primary hover:underline flex-shrink-0"
        >
          Clear all
        </button>
      )}
    </div>,
    document.body
  );
};

export default QuickFilterBar;
